import { Suspense, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Phone, Mail, ExternalLink, Edit } from 'lucide-react';

import { Card } from '../components/ui/card.tsx';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs.tsx';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '../components/ui/avatar.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Progress } from '../components/ui/progress.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';
import { StudentEditPanel } from '../components/students/StudentEditPanel';
import { ParentEditPanel } from '../components/parent/ParentEditPanel';
// Import useGetChildById from studentQueries.js instead of studentHooks.js

import { useGetChildById } from '../api/student/studentQueries';
import { useGetAllRecordTypes } from '../api/recordTypes/recordTypesQueries.js';

// Add sample data for each tab
const exams = [
  {
    id: '1',
    teacher: {
      name: 'Prof. Adeniran',
      avatar: '/placeholder.svg',
    },
    subject: 'Physics',
    score: 92,
    comment: 'Excellent understanding of wave mechanics and optics.',
    date: '20/Apr/2024',
    type: 'Final Exam',
    topic: 'Wave Properties',
  },
  {
    id: '2',
    teacher: {
      name: 'Mrs. Okonjo',
      avatar: '/placeholder.svg',
    },
    subject: 'Chemistry',
    score: 88,
    comment: 'Good grasp of organic chemistry concepts.',
    date: '18/Apr/2024',
    type: 'Mid-term',
    topic: 'Organic Chemistry',
  },
];

const assignments = [
  {
    id: '1',
    teacher: {
      name: 'Mr. Olawale',
      avatar: '/placeholder.svg',
    },
    subject: 'Biology',
    score: 95,
    comment: 'Very detailed lab report on cell structure.',
    date: '12/Apr/2024',
    type: 'Lab Report',
    topic: 'Cell Biology',
  },
  {
    id: '2',
    teacher: {
      name: 'Mrs. Adebayo',
      avatar: '/placeholder.svg',
    },
    subject: 'Literature',
    score: 85,
    comment: 'Well-written essay, good analysis of themes.',
    date: '10/Apr/2024',
    type: 'Essay',
    topic: 'Things Fall Apart',
  },
];

const classes = [
  {
    id: '1',
    teacher: {
      name: 'Mr. Johnson',
      avatar: '/placeholder.svg',
    },
    subject: 'Mathematics',
    attendance: 'Present',
    comment: 'Active participation in class discussion.',
    date: '15/Apr/2024',
    type: 'Regular',
    topic: 'Calculus',
  },
  {
    id: '2',
    teacher: {
      name: 'Mrs. Elizabeth',
      avatar: '/placeholder.svg',
    },
    subject: 'English',
    attendance: 'Absent',
    comment: 'Missed important grammar lesson.',
    date: '14/Apr/2024',
    type: 'Regular',
    topic: 'Grammar',
  },
];

const results = [
  {
    id: '1',
    teacher: {
      name: 'Ajibade Babalola',
      avatar: '/placeholder.svg',
    },
    subject: 'Math',
    score: 80,
    comment: 'Great Job Tope, I like your performance so far.',
    date: '14/Apr/2024',
    type: 'Exams',
    topic: 'Quadratic Equation',
  },
  {
    id: '2',
    teacher: {
      name: 'Goerge Anthony',
      avatar: '/placeholder.svg',
    },
    subject: 'Yoruba',
    score: 60,
    comment: "I think there's so much you can improve on",
    date: '14/Apr/2024',
    type: 'Assignment',
    topic: 'Ami Ori Oro',
  },
];

function StudentDetailsContent({ studentId }) {
  // Use the enhanced useGetChildById hook with better error handling and data normalization
  const { data: student, isLoading, error } = useGetChildById(studentId);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [isParentEditPanelOpen, setIsParentEditPanelOpen] = useState(false);
  const [showScore, setShowScore] = useState(true);

  const {
    data: recordTypesData,
    isLoading: isRecordTypesLoading,
    isError: isRecordTypesError,
  } = useGetAllRecordTypes();

  // Handle loading state
  if (isLoading || isRecordTypesLoading) {
    return (
      <div className="flex">
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mb-4" />
            <p className="text-muted-foreground">Loading student details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state - specifically for ObjectID errors
  if (error) {
    return (
      <div className="flex">
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-4">
              <p className="font-semibold mb-2">Error loading student data</p>
              <p>{error.message || 'An unknown error occurred'}</p>
              {error.message?.includes('ObjectID') && (
                <p className="mt-2 text-sm">
                  The student ID provided is invalid or missing.
                </p>
              )}
            </div>
            <Button onClick={() => window.history.back()} variant="secondary">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Handle case when student data is not available
  if (!student) {
    return (
      <div className="flex">
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="bg-yellow-100 text-yellow-800 p-4 rounded-lg mb-4">
              <p className="font-semibold mb-2">Student Not Found</p>
              <p>The requested student record could not be found.</p>
            </div>
            <Button onClick={() => window.history.back()} variant="secondary">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  console.log('Student Data in Component:', student);

  const renderTabContent = (items, showScore = true) => (
    <div className="space-y-4">
      {items.map((item) => (
        <Card key={item.id} className="p-4">
          <div className="flex items-start gap-4">
            <Avatar>
              <AvatarImage src={item.teacher.avatar} alt={item.teacher.name} />
              <AvatarFallback>{item.teacher.name[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex justify-between">
                <div>
                  <p className="font-medium">{item.teacher.name}</p>
                  <p className="text-sm text-purple-600">{item.subject}</p>
                </div>
                <Button variant="ghost" size="icon">
                  <ExternalLink className="h-4 w-4 text-purple-600" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {item.comment}
              </p>
              <div className="flex justify-between items-center mt-4">
                <Badge variant="outline" className="bg-purple-50">
                  {item.topic}
                </Badge>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="secondary"
                    className="bg-purple-100 text-purple-700"
                  >
                    {item.type}
                  </Badge>
                  {showScore ? (
                    <Badge
                      className={`${
                        item.score >= 90
                          ? 'bg-green-100 text-green-700'
                          : item.score >= 70
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {item.score}%
                    </Badge>
                  ) : (
                    <Badge
                      className={
                        item.attendance === 'Present'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }
                    >
                      {item.attendance}
                    </Badge>
                  )}
                  <p className="text-sm text-muted-foreground">{item.date}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  // The student data is already normalized by our enhanced useGetChildById hook
  const studentName = student.name;
  const parentName = student.parent
    ? `${student.parent.firstName || ''} ${
        student.parent.lastName || ''
      }`.trim() || 'Not Assigned'
    : 'Not Assigned';

  // Unified handler for opening edit panel
  const handleOpenEditPanel = () => {
    setIsEditPanelOpen(true);
  };

  return (
    <div className="flex">
      <div className="flex-1 p-6 max-w-[1200px] mx-auto">
        <div className="mb-4">
          <Button
            variant="ghost"
            className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            onClick={() => window.history.back()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Students
          </Button>
        </div>

        <Card className="p-6 mb-8">
          <div className="flex gap-6">
            <Avatar className="w-24 h-24">
              <AvatarImage src={student?.photoUrl} />
              <AvatarFallback>{studentName.charAt(0) || '?'}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-2xl font-bold mb-2">{studentName}</h1>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>Student ID: {student?.childId}</p>
                    <p>Organization ID: {student?.organizationId}</p>
                    <p className="text-primary">
                      Class: {student?.class || 'Not Assigned'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" size="icon">
                    <Phone className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon">
                    <Mail className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="self-start"
              onClick={handleOpenEditPanel}
            >
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        <div className="grid grid-cols-[1fr_300px] gap-6">
          <div>
            <Tabs defaultValue="general" className="mb-6">
              <div className="flex justify-between items-center mb-6">
                <TabsList>
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="exams">Exams</TabsTrigger>
                  <TabsTrigger value="assignment">Assignment</TabsTrigger>
                  <TabsTrigger value="classes">Classes</TabsTrigger>
                </TabsList>
                {/* <Button variant="secondary">Edit Buckets</Button> */}
              </div>

              <TabsContent value="general">
                <div className="mb-4 flex justify-between items-center">
                  <div className="flex gap-6">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-green-100">
                        Passed: 250
                      </Badge>
                      <Badge variant="secondary" className="bg-red-100">
                        Failed: 50
                      </Badge>
                      <Badge variant="secondary" className="bg-gray-100">
                        Missed: 10
                      </Badge>
                    </div>
                    <Select>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Subject" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="math">Math</SelectItem>
                        <SelectItem value="english">English</SelectItem>
                        <SelectItem value="science">Science</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      7 days
                    </Button>
                    <Button variant="outline" size="sm">
                      30 days
                    </Button>
                    <Button variant="secondary" size="sm">
                      All
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  {results.map((result) => (
                    <Card key={result.id} className="p-4">
                      <div className="flex items-start gap-4">
                        <Avatar>
                          <img
                            src={result.teacher.avatar}
                            alt={result.teacher.name}
                          />
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <div>
                              <p className="font-medium">
                                {result.teacher.name}
                              </p>
                              <p className="text-sm text-primary">
                                {result.subject}
                              </p>
                            </div>
                            <Button variant="ghost" size="icon">
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {result.comment}
                          </p>
                          <div className="flex justify-between items-center mt-4">
                            <Badge variant="outline">{result.topic}</Badge>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">{result.type}</Badge>
                              <p className="text-sm text-muted-foreground">
                                {result.date}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="exams">
                <div className="mb-4 flex justify-between items-center"></div>
                {renderTabContent(exams)}
              </TabsContent>

              <TabsContent value="assignment">
                <div className="mb-4 flex justify-between items-center"></div>
                {renderTabContent(assignments)}
              </TabsContent>

              <TabsContent value="classes">
                <div className="mb-4 flex justify-between items-center"></div>
                {renderTabContent(classes, false)}
              </TabsContent>
            </Tabs>
          </div>

          <div className="space-y-6">
            <Card className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Fees</h3>
                <Button variant="ghost" size="icon">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-2xl font-bold text-red-500">-N590,698</p>
            </Card>

            <Card className="p-4">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold">Performance</h3>
                <p className="text-2xl font-bold text-green-500">85%</p>
              </div>
              <div className="space-y-4">
                {[
                  'Math',
                  'English',
                  'Yoruba',
                  'Civic Education',
                  'Physics',
                ].map((subject) => (
                  <div key={subject}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{subject}</span>
                      <span className="text-muted-foreground">85%</span>
                    </div>
                    <Progress value={85} />
                  </div>
                ))}
                <Button variant="link" className="w-full">
                  All Subjects
                </Button>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Parents / Caretakers</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsParentEditPanelOpen(true)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarImage
                    src={student?.parent?.photoUrl || '/placeholder.svg'}
                    alt={parentName}
                  />
                  <AvatarFallback>{parentName[0] || '?'}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{parentName}</p>
                  {student?.parent?.email && (
                    <p className="text-sm text-muted-foreground">
                      {student.parent.email}
                    </p>
                  )}
                </div>
                <Button variant="ghost" size="icon">
                  <Phone className="h-4 w-4" />
                </Button>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Documents</h3>
                <Button variant="ghost" size="icon">
                  <Edit className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="h-auto py-4 px-3">
                  <div className="text-left">
                    <p className="font-medium">Parent ID</p>
                    <p className="text-sm text-muted-foreground">
                      View document
                    </p>
                  </div>
                </Button>
                <Button variant="outline" className="h-auto py-4 px-3">
                  <div className="text-left">
                    <p className="font-medium">Birth Cert</p>
                    <p className="text-sm text-muted-foreground">
                      View document
                    </p>
                  </div>
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
      <StudentEditPanel
        student={student}
        isOpen={isEditPanelOpen}
        onClose={() => setIsEditPanelOpen(false)}
      />

      <ParentEditPanel
        parent={student?.parent}
        isOpen={isParentEditPanelOpen}
        onClose={() => setIsParentEditPanelOpen(false)}
      />
    </div>
  );
}

export default function StudentProfile() {
  const { studentId } = useParams();

  return (
    <Suspense
      fallback={
        <div className="flex">
          <div className="flex-1 p-6 flex items-center justify-center">
            <div className="text-center">
              <LoadingSpinner size="lg" className="mb-4" />
              <p className="text-muted-foreground">
                Loading student profile...
              </p>
            </div>
          </div>
        </div>
      }
    >
      <StudentDetailsContent studentId={studentId} />
    </Suspense>
  );
}
